import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Calendar,
  FileSpreadsheet,
  Printer,
  Users,
  DollarSign,
  ChevronRight
} from 'lucide-react';
import { format, parseISO, startOfWeek, endOfWeek, addDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const diasSemana = [
  { key: 'segunda', label: 'Segunda-feira', short: 'Seg', color: 'bg-blue-500' },
  { key: 'terca', label: 'Terça-feira', short: 'Ter', color: 'bg-green-500' },
  { key: 'quarta', label: 'Quarta-feira', short: 'Qua', color: 'bg-yellow-500' },
  { key: 'quinta', label: 'Quinta-feira', short: 'Qui', color: 'bg-orange-500' },
  { key: 'sexta', label: 'Sexta-feira', short: 'Sex', color: 'bg-red-500' },
  { key: 'sabado', label: 'Sábado', short: 'Sáb', color: 'bg-purple-500' },
  { key: 'domingo', label: 'Domingo', short: 'Dom', color: 'bg-gray-500' }
];

export default function WeeklyReportPage() {
  const { API } = useAuth();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [groupedByDay, setGroupedByDay] = useState({});
  const [selectedDay, setSelectedDay] = useState(null);
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));

  useEffect(() => {
    loadInvoices();
  }, [weekStart]);

  const loadInvoices = async () => {
    setLoading(true);
    try {
      // Faturas a vencer SOMENTE da semana exibida (antes baixava todas e agrupava por
      // dia da semana misturando semanas diferentes)
      const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
      const response = await axios.get(`${API}/invoices`, {
        params: {
          status: 'a_vencer',
          data_inicio: format(weekStart, 'yyyy-MM-dd'),
          data_fim: format(weekEnd, 'yyyy-MM-dd'),
        },
        withCredentials: true
      });
      setInvoices(response.data);
      groupInvoicesByWeekday(response.data);
    } catch (error) {
      toast.error('Erro ao carregar faturas');
    } finally {
      setLoading(false);
    }
  };

  const groupInvoicesByWeekday = (data) => {
    const grouped = {
      segunda: [],
      terca: [],
      quarta: [],
      quinta: [],
      sexta: [],
      sabado: [],
      domingo: []
    };

    const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });

    data.forEach(invoice => {
      if (!invoice.vencimento) return;
      
      try {
        const invoiceDate = parseISO(invoice.vencimento);
        const dayIndex = invoiceDate.getDay();
        
        // Map JS day (0=Sunday) to our array (0=Monday)
        const dayMap = {
          0: 'domingo',
          1: 'segunda',
          2: 'terca',
          3: 'quarta',
          4: 'quinta',
          5: 'sexta',
          6: 'sabado'
        };

        const dayKey = dayMap[dayIndex];
        
        // Extract readable vehicle ID from vehicle_info
        const vehicleDisplay = invoice.vehicle_info 
          ? invoice.vehicle_info.split(' - ')[0]
          : invoice.vehicle_id;

        grouped[dayKey].push({
          invoice_id: invoice.invoice_id,
          vencimento: invoice.vencimento,
          client_name: invoice.client_name,
          vehicle_id: vehicleDisplay,
          vehicle_info: invoice.vehicle_info,
          valor: invoice.valor_original || 0
        });
      } catch (e) {
        console.error('Error parsing date:', e);
      }
    });

    // Sort each day's invoices by client name
    Object.keys(grouped).forEach(day => {
      grouped[day].sort((a, b) => (a.client_name || '').localeCompare(b.client_name || ''));
    });

    setGroupedByDay(grouped);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(parseISO(dateStr), 'dd/MM/yyyy', { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  const getTotalByDay = (dayKey) => {
    return groupedByDay[dayKey]?.reduce((acc, inv) => acc + inv.valor, 0) || 0;
  };

  const getTotalWeek = () => {
    return Object.values(groupedByDay).flat().reduce((acc, inv) => acc + inv.valor, 0);
  };

  const getTotalInvoices = () => {
    return Object.values(groupedByDay).flat().length;
  };

  const exportToCSV = () => {
    const headers = ['Dia da Semana', 'Veículo', 'Cliente', 'Vencimento', 'Valor'];
    const rows = [];
    
    diasSemana.forEach(dia => {
      groupedByDay[dia.key]?.forEach(inv => {
        rows.push([
          dia.label,
          inv.vehicle_id,
          inv.client_name,
          formatDate(inv.vencimento),
          inv.valor
        ]);
      });
    });
    
    const csvContent = [
      headers.join(','),
      ...rows.map(r => r.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `relatorio_semanal_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
    toast.success('CSV exportado!');
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-6" data-testid="weekly-report-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 font-[Manrope]">Relatório Semanal</h1>
          <p className="text-gray-500 text-sm">Vencimentos de faturas organizados por dia da semana</p>
        </div>
        
        <div className="flex gap-2 print:hidden">
          <Button variant="outline" size="sm" onClick={exportToCSV}>
            <FileSpreadsheet className="w-4 h-4 mr-1" />
            Exportar CSV
          </Button>
          <Button size="sm" onClick={handlePrint}>
            <Printer className="w-4 h-4 mr-1" />
            Imprimir
          </Button>
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-white border-t-4 border-t-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total de Faturas</p>
                <p className="text-3xl font-bold text-blue-600">{getTotalInvoices()}</p>
                <p className="text-xs text-gray-400">faturas a vencer</p>
              </div>
              <Calendar className="w-10 h-10 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-t-4 border-t-green-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Valor Total Semanal</p>
                <p className="text-3xl font-bold text-green-600">{formatCurrency(getTotalWeek())}</p>
                <p className="text-xs text-gray-400">a receber</p>
              </div>
              <DollarSign className="w-10 h-10 text-green-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-t-4 border-t-purple-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Contratos Ativos</p>
                <p className="text-3xl font-bold text-purple-600">
                  {new Set(Object.values(groupedByDay).flat().map(i => i.vehicle_id)).size}
                </p>
                <p className="text-xs text-gray-400">veículos com vencimentos</p>
              </div>
              <Users className="w-10 h-10 text-purple-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          {/* Week Day Cards */}
          <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
            {diasSemana.map((dia) => {
              const count = groupedByDay[dia.key]?.length || 0;
              const total = getTotalByDay(dia.key);
              const isSelected = selectedDay === dia.key;
              
              return (
                <Card 
                  key={dia.key}
                  className={`cursor-pointer transition-all hover:shadow-lg ${
                    isSelected ? 'ring-2 ring-blue-500 shadow-lg' : ''
                  } ${count === 0 ? 'opacity-50' : ''}`}
                  onClick={() => setSelectedDay(isSelected ? null : dia.key)}
                  data-testid={`day-card-${dia.key}`}
                >
                  <CardContent className="p-4 text-center">
                    <div className={`w-10 h-10 ${dia.color} rounded-full mx-auto mb-2 flex items-center justify-center`}>
                      <span className="text-white font-bold text-sm">{dia.short}</span>
                    </div>
                    <h3 className="font-semibold text-gray-800 text-sm">{dia.label}</h3>
                    <p className="text-2xl font-bold text-gray-900 mt-1">{count}</p>
                    <p className="text-xs text-gray-500">faturas</p>
                    <p className="text-sm font-semibold text-green-600 mt-2">
                      {formatCurrency(total)}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Detail Table - Shows when a day is selected or all if none selected */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                {selectedDay 
                  ? `Vencimentos de ${diasSemana.find(d => d.key === selectedDay)?.label}`
                  : 'Todos os Vencimentos por Dia'
                }
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {selectedDay ? (
                // Single day view
                groupedByDay[selectedDay]?.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Nenhum vencimento para este dia</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="font-semibold">Veículo</TableHead>
                        <TableHead className="font-semibold">Cliente</TableHead>
                        <TableHead className="font-semibold text-center">Vencimento</TableHead>
                        <TableHead className="font-semibold text-right">Valor</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {groupedByDay[selectedDay]?.map((invoice, idx) => (
                        <TableRow key={invoice.invoice_id || idx} className="hover:bg-gray-50">
                          <TableCell className="font-medium text-blue-600">{invoice.vehicle_id}</TableCell>
                          <TableCell>{invoice.client_name}</TableCell>
                          <TableCell className="text-center">{formatDate(invoice.vencimento)}</TableCell>
                          <TableCell className="text-right font-semibold text-green-600">
                            {formatCurrency(invoice.valor)}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-gray-100 font-bold">
                        <TableCell colSpan={3} className="text-right">Total do Dia:</TableCell>
                        <TableCell className="text-right text-green-700">
                          {formatCurrency(getTotalByDay(selectedDay))}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                )
              ) : (
                // All days view - grouped
                <div className="divide-y">
                  {diasSemana.map(dia => {
                    const dayInvoices = groupedByDay[dia.key] || [];
                    if (dayInvoices.length === 0) return null;
                    
                    return (
                      <div key={dia.key} className="p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <div className={`w-8 h-8 ${dia.color} rounded-full flex items-center justify-center`}>
                            <span className="text-white font-bold text-xs">{dia.short}</span>
                          </div>
                          <h3 className="font-semibold text-gray-800">{dia.label}</h3>
                          <span className="px-2 py-0.5 bg-gray-100 rounded-full text-xs text-gray-600">
                            {dayInvoices.length} faturas
                          </span>
                          <span className="ml-auto font-semibold text-green-600">
                            {formatCurrency(getTotalByDay(dia.key))}
                          </span>
                        </div>
                        
                        <div className="ml-10 space-y-1">
                          {dayInvoices.slice(0, 5).map((inv, idx) => (
                            <div key={inv.invoice_id || idx} className="flex items-center justify-between py-1 border-b border-gray-100 last:border-0">
                              <div className="flex items-center gap-3">
                                <span className="text-blue-600 font-medium w-24">{inv.vehicle_id}</span>
                                <span className="text-gray-700">{inv.client_name}</span>
                              </div>
                              <span className="text-green-600 font-medium">{formatCurrency(inv.valor)}</span>
                            </div>
                          ))}
                          {dayInvoices.length > 5 && (
                            <button 
                              onClick={() => setSelectedDay(dia.key)}
                              className="text-blue-600 text-sm hover:underline flex items-center gap-1"
                            >
                              Ver mais {dayInvoices.length - 5} faturas
                              <ChevronRight className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
